import { CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Bank } from "@/apis/gql/types";
import type { TranslationKey } from "@/localisation/types";

import { getAggregatedCapabilities } from "../utils";

type CapabilityItem = {
  condition: boolean;
  translationKey: TranslationKey;
};

const CapabilityListItem = ({
  translationKey,
}: { translationKey: TranslationKey }) => {
  const { t } = useTranslation();
  return (
    <li className="flex items-center text-sm text-gray-700">
      <CheckCircle2 className="h-4 w-4 mr-2 text-emerald-600" />
      <span>{t(translationKey) as string}</span>
    </li>
  );
};

export const ConnectionCompleteStep = ({
  bank,
  error,
  abort,
  success,
}: {
  bank: Bank;
  error: boolean;
  abort: boolean;
  success: boolean;
}) => {
  const { t } = useTranslation();

  const capabilities = getAggregatedCapabilities(bank);

  if (!capabilities) {
    return (
      <div>{t("bank_connections.connection_step_one.no_capabilities")}</div>
    );
  }

  const capabilityItems: CapabilityItem[] = [
    {
      condition: capabilities.isPisSupported,
      translationKey: "bank_connections.connection_step_two.sepa_payments",
    },
    {
      condition: capabilities.paymentCapabilities?.sepaInstantMoneyTransfer,
      translationKey: "bank_connections.connection_step_two.instant_transfers",
    },
    {
      condition: capabilities.paymentCapabilities?.sepaCollectiveMoneyTransfer,
      translationKey:
        "bank_connections.connection_step_two.collective_transfers",
    },
    {
      condition: capabilities.paymentCapabilities?.sepaStandingOrder,
      translationKey: "bank_connections.connection_step_two.standing_orders",
    },
    {
      condition: capabilities.isAisSupported,
      translationKey:
        "bank_connections.connection_step_two.account_information_access",
    },
    {
      condition: capabilities.aisCapabilities?.standingOrdersDownload,
      translationKey:
        "bank_connections.connection_step_two.standing_orders_download",
    },
    {
      condition: capabilities.aisCapabilities?.pendingTransactionsDownload,
      translationKey:
        "bank_connections.connection_step_two.pending_transactions_download",
    },
  ];

  return (
    <div className="text-center space-y-6">
      <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle2 className="h-8 w-8 text-emerald-600" />
      </div>
      <h3 className="text-xl font-medium text-gray-900">
        {t("bank_connections.connection_step_two.title")}
      </h3>
      <p className="text-gray-600">
        {t("bank_connections.connection_step_two.description")}
      </p>

      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-left">
        <h4 className="font-medium text-gray-900 mb-2">
          {t("bank_connections.connection_step_two.available_features")}
        </h4>
        <ul className="space-y-2">
          {capabilityItems
            .filter((item) => item.condition)
            .map((item, index) => (
              <CapabilityListItem
                // biome-ignore lint/suspicious/noArrayIndexKey: not a problem in this case
                key={index}
                translationKey={item.translationKey}
              />
            ))}
        </ul>
      </div>
    </div>
  );
};
